import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import moment from 'moment'
import 'moment/locale/ko'
import { CHART_COLORS, CHART_DATE_FORMAT, CHART_UNIT } from './chartConstants'
import CustomTooltip from './CustomTootip'
import { useSelector } from 'react-redux'
import { getChartUnit } from './chartUtil'

const tenThousand = 10000
const hundredMil = tenThousand * tenThousand
const toYAxisValue = value => {
  const quotient =
    value >= hundredMil
      ? value / hundredMil
      : value >= tenThousand
      ? value / tenThousand
      : value
  const unit = value >= hundredMil ? '억' : value >= tenThousand ? '만' : ''
  return quotient.toLocaleString() + unit
}
const toXAxisDate = date => moment(date, CHART_DATE_FORMAT).format('MM-DD')
const toXAxisHour = hour => `${String(hour).padStart(2, '0')}시`

const Chart = () => {
  const { current } = useSelector(state => state.account)
  const createdDate = current?.createdDate
  const {
    common: { from, to },
    chart: { data, metricColumns },
  } = useSelector(state => state.dashboard)
  const start = createdDate
    ? moment
        .max(moment(createdDate, 'YYYY-MM-DD[T]HH:mm:ss'), moment(from))
        .format(moment.HTML5_FMT.DATE)
    : from
  const [left, right] = metricColumns
  const chartUnit = useMemo(() => getChartUnit(start, to), [start, to])
  const showRight = useMemo(() => left !== right, [left, right])
  return (
    <ResponsiveContainer width="100%" height={259}>
      <AreaChart
        data={data}
        margin={{
          top: 5,
          right: 15,
          bottom: 0,
          left: 15,
        }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        {chartUnit === CHART_UNIT.DAY ? (
          <XAxis
            dataKey="dateId"
            tickLine={false}
            tick={{
              fontSize: 11,
              fontWeight: 'bold',
            }}
            tickFormatter={toXAxisDate}
          />
        ) : (
          <XAxis
            dataKey="hour"
            tickLine={false}
            tick={{
              fontSize: 11,
              fontWeight: 'bold',
            }}
            tickFormatter={toXAxisHour}
          />
        )}
        <YAxis
          dataKey={left}
          yAxisId="left"
          type="number"
          axisLine={false}
          width={35}
          tick={{
            fill: CHART_COLORS.LEFT,
            fontSize: 11,
          }}
          tickLine={{ stroke: 'lightgray' }}
          tickSize={5}
          tickFormatter={toYAxisValue}
        />
        {showRight && (
          <YAxis
            dataKey={right}
            yAxisId="right"
            type="number"
            orientation="right"
            axisLine={false}
            width={35}
            tick={{
              fill: CHART_COLORS.RIGHT,
              fontSize: 11,
            }}
            tickLine={{ stroke: 'lightgray' }}
            tickSize={5}
            tickFormatter={toYAxisValue}
          />
        )}
        <Tooltip content={<CustomTooltip chartUnit={chartUnit} />} />
        <Area
          dataKey={left}
          yAxisId="left"
          type="monotone"
          stroke={CHART_COLORS.LEFT}
          fill={CHART_COLORS.LEFT}
          fillOpacity={0.07}
          activeDot={{ r: 5 }}
          dot={{
            r: 2,
            fill: CHART_COLORS.LEFT,
            fillOpacity: 1,
          }}
        />
        {showRight && (
          <Area
            dataKey={right}
            yAxisId="right"
            type="monotone"
            stroke={CHART_COLORS.RIGHT}
            fill={CHART_COLORS.RIGHT}
            fillOpacity={0.07}
            activeDot={{ r: 5 }}
            dot={{
              r: 2,
              fill: CHART_COLORS.RIGHT,
              fillOpacity: 1,
            }}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  )
}
export default Chart
